import React, { useState } from 'react';
import { CheckIcon, Loader2Icon } from 'lucide-react';
import { SeoHead } from '../components/SeoHead';
type Status = 'idle' | 'submitting' | 'success' | 'error';
type FormState = {
  name: string;
  email: string;
  organisation: string;
  project: string;
  budget: string;
  timing: string;
};
const budgets = ['Under $100k', '$100k – $300k', '$300k – $1m', '$1m+', 'Not sure yet'];

/**
 * Paste the Formspree form ID here (the part after /f/ in the endpoint they
 * give you, e.g. 'abcdwxyz'). Until it is set, the form tells people to email
 * instead of silently dropping their brief.
 */
const FORMSPREE_ID = '';
const initialState: FormState = {
  name: '',
  email: '',
  organisation: '',
  project: '',
  budget: '',
  timing: ''
};
export function Contact() {
  const [form, setForm] = useState<FormState>(initialState);
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  function update(field: keyof FormState, value: string) {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  }
  function validate() {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = 'Please tell us your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Please enter a valid email address.';
    if (form.project.trim().length < 20) next.project = 'A couple of sentences helps us reply properly.';
    return next;
  }
  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    if (!FORMSPREE_ID) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          organisation: form.organisation,
          project: form.project,
          budget: form.budget || 'Not specified',
          timing: form.timing || 'Not specified',
          _subject: `New brief from ${form.name}${form.organisation ? ` — ${form.organisation}` : ''}`
        })
      });
      if (!response.ok) throw new Error('Submission failed');
      setStatus('success');
      setForm(initialState);
    } catch {
      setStatus('error');
    }
  }
  const inputClass = 'w-full border-b border-line bg-transparent py-3 outline-none transition-colors placeholder:text-muted/60 focus:border-sage';
  return <div className="mx-auto max-w-page px-6 py-12 md:px-10 md:py-16" data-id="element-145" data-mp="kpkz3c2j17c5">
      <SeoHead title="Contact Breezy Experiential — Start an Experiential Project" description="Tell us about your pop-up, tour, activation, tradeshow or conference. Breezy Experiential replies to every brief within two working days. Email info@heybreezy.co." data-id="element-146" data-mp="frb0ssli9qmb" />

      <header className="flex flex-wrap items-end justify-between gap-6" data-id="element-147" data-mp="2w76buysc8vwr">
        <h1 className="font-display text-4xl font-bold uppercase leading-[0.9] md:text-7xl" data-id="element-148" data-mp="2hi1tnhljmron">
          Start a
          <br data-id="element-149" data-mp="2mdh3zzkce5gb" />
          <span className="text-lilac" data-id="element-150" data-mp="2md7ai2039g5v">project</span>
        </h1>
        <div className="text-sm text-muted" data-id="element-151" data-mp="3a5nw2mj1bq9x">
          <a href="mailto:info@heybreezy.co" className="font-display text-xl uppercase text-cream underline underline-offset-4 transition-colors hover:text-sage md:text-2xl" data-id="element-152" data-mp="3f3z4qtxlfuqk">
            info@heybreezy.co
          </a>
          <p className="mt-2" data-id="element-153" data-mp="354gnwmczcp2q">We reply to every brief within two working days.</p>
        </div>
      </header>

      <div className="mt-12 border-t border-line pt-10" data-id="element-154" data-mp="1pnzq4xb7hd3j">
        {status === 'success' ? <div role="status" className="rounded-[1.5rem] bg-sage p-8 text-ink md:p-12" data-id="element-155" data-mp="1ts7h7154z54x">
            <CheckIcon size={26} data-id="element-156" data-mp="29zlq5v0vyun0" />
            <h2 className="mt-4 font-display text-3xl font-bold uppercase leading-[0.95] md:text-4xl" data-id="element-157" data-mp="29zu6xy04qk3h">
              Thanks — that&apos;s with us
            </h2>
            <p className="mt-3 max-w-md leading-relaxed text-ink/75" data-id="element-158" data-mp="208r34oftfr0i">
              One of us will read it properly and come back within two working
              days, usually with questions rather than a deck.
            </p>
            <button type="button" onClick={() => setStatus('idle')} className="mt-7 text-[0.7rem] uppercase tracking-micro underline underline-offset-4 hover:text-ink/60" data-id="element-159" data-mp="2suivxb7hkjx0">
              Send another brief
            </button>
          </div> : <form onSubmit={handleSubmit} noValidate className="grid gap-8 md:grid-cols-12" data-id="element-160" data-mp="17jk5rss3l7gu">
            <div className="md:col-span-7 space-y-8" data-id="element-161" data-mp="13gvw5ivk7wit">
              <div className="grid gap-8 sm:grid-cols-2" data-id="element-162" data-mp="13kbc0nx259la">
                <div data-id="element-163" data-mp="3f6ligj7skmnk">
                  <label htmlFor="name" className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-164" data-mp="3f37i1giounir">
                    Name
                  </label>
                  <input id="name" value={form.name} onChange={e => update('name', e.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} className={inputClass} placeholder="Your name" data-id="element-165" data-mp="3o45m1yt4mv1c" />
                  {errors.name && <p id="name-error" className="mt-2 text-sm text-red" data-id="element-166" data-mp="fqf8f4l1r76f">
                      {errors.name}
                    </p>}
                </div>
                <div data-id="element-167" data-mp="2hha4lxj618do">
                  <label htmlFor="email" className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-168" data-mp="2hgqommd2su2a">
                    Email
                  </label>
                  <input id="email" type="email" value={form.email} onChange={e => update('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} className={inputClass} placeholder="you@company.com" data-id="element-169" data-mp="3fuo3z3fr79kn" />
                  {errors.email && <p id="email-error" className="mt-2 text-sm text-red" data-id="element-170" data-mp="gd6ayhqzsfrt">
                      {errors.email}
                    </p>}
                </div>
              </div>

              <div className="grid gap-8 sm:grid-cols-2" data-id="element-171" data-mp="qbm5yvt5496l">
                <div data-id="element-172" data-mp="qa2k8popt5xf">
                  <label htmlFor="organisation" className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-173" data-mp="lducvygreh0t">
                    Company
                  </label>
                  <input id="organisation" value={form.organisation} onChange={e => update('organisation', e.target.value)} className={inputClass} placeholder="Optional" data-id="element-174" data-mp="19x5uihbyd75j" />
                </div>
                <div data-id="element-175" data-mp="m71qi8uur5e0">
                  <label htmlFor="timing" className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-176" data-mp="m6jmur5v1ow2">
                    Timing
                  </label>
                  <input id="timing" value={form.timing} onChange={e => update('timing', e.target.value)} className={inputClass} placeholder="e.g. Fall 2026" data-id="element-177" data-mp="h9jfmcx9yg3d" />
                </div>
              </div>

              <div data-id="element-178" data-mp="nrefutz0zs4b">
                <label htmlFor="project" className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-179" data-mp="nptn99w9uj93">
                  What are you planning?
                </label>
                <textarea id="project" rows={5} value={form.project} onChange={e => update('project', e.target.value)} aria-invalid={Boolean(errors.project)} aria-describedby={errors.project ? 'project-error' : undefined} className={`${inputClass} resize-none`} placeholder="The audience, the occasion, and what you want them to walk away with." data-id="element-180" data-mp="dr45dh4dkysu" />
                {errors.project && <p id="project-error" className="mt-2 text-sm text-red" data-id="element-181" data-mp="3h95q2quxy40f">
                    {errors.project}
                  </p>}
              </div>
            </div>

            <div className="md:col-span-4 md:col-start-9" data-id="element-182" data-mp="1sprrudz82lyk">
              <fieldset data-id="element-183" data-mp="1sp8fdcsccprx">
                <legend className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-184" data-mp="1ss47ullf6m8z">
                  Budget range
                </legend>
                <div className="mt-4 flex flex-wrap gap-2" data-id="element-185" data-mp="21rhllqgktwzi">
                  {budgets.map(budget => {
                const active = form.budget === budget;
                return <button key={budget} type="button" onClick={() => update('budget', active ? '' : budget)} aria-pressed={active} className={`rounded-full border px-4 py-2 text-[0.6rem] uppercase tracking-micro transition-colors ${active ? 'border-sage bg-sage text-ink' : 'border-line text-muted hover:border-cream hover:text-cream'}`} data-id="element-186" data-mp="192k6fw7nwgaz">
                        {budget}
                      </button>;
              })}
                </div>
              </fieldset>

              {status === 'error' && <p role="alert" className="mt-6 text-sm text-red" data-id="element-187" data-mp="yv8l8dn4oik1">
                  We couldn&apos;t send that. Please email your brief to{' '}
                  <a href="mailto:info@heybreezy.co" className="underline underline-offset-4" data-id="element-188" data-mp="13u3d0532phga">
                    info@heybreezy.co
                  </a>
                  .
                </p>}

              <button type="submit" disabled={status === 'submitting'} className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-lilac px-8 py-4 text-[0.65rem] uppercase tracking-micro text-ink transition-colors hover:bg-sage disabled:opacity-60" data-id="element-189" data-mp="g491bquhpyxm">
                {status === 'submitting' && <Loader2Icon size={14} className="animate-spin" data-id="element-190" data-mp="25qhdnuoc19kv" />}
                {status === 'submitting' ? 'Sending' : 'Send brief'}
              </button>

              <p className="mt-6 text-sm leading-relaxed text-muted" data-id="element-191" data-mp="1wpavzi9y5sq1">
                National tours and activations, coast to coast — routing,
                permitting, freight and on-site crew handled in-house.
              </p>
            </div>
          </form>}
      </div>
    </div>;
}